// [2026-08-12] ADDED — InC-Proctor sign-in handoff.
//
// THE FLOW
// --------
//   1. This saga POSTs to the proctoring bridge with the operator's live InCruiter admin session.
//   2. The bridge verifies that session against InCruiter's own token store, decides the role
//      SERVER-SIDE from the user record, and returns a SINGLE-USE code — never a session token.
//   3. The container navigates to the proctoring dashboard with that code in the URL FRAGMENT,
//      which the dashboard spends once and immediately strips.
//
// WHY THE CODE GOES IN A FRAGMENT AND NOT A QUERY STRING
// -----------------------------------------------------
// A fragment is never sent to any server, so the code stays out of access logs, proxy logs and the
// `Referer` header of anything the dashboard subsequently loads. It is single-use and expires in
// seconds regardless — this is defence in depth that costs nothing.
//
// The code is passed straight to the redirect and never enters Redux, because Redux state in this
// app is persisted to localStorage under the key "root".

import { all, takeEvery, put, call } from "redux-saga/effects";

import { PROCTOR_HANDOFF } from "../action/types";
import {
  proctorHandoffSuccess,
  proctorHandoffFailure,
} from "../action/proctoringActions";
import PROCTORING from "../../utils/api/proctoring";
import { generateUrl } from "../../utils/helper";

function* proctorHandoffRequest(action) {
  const payload = action.payload || {};
  try {
    // [2026-09-07] CHANGED — the endpoint that exists, and no credential in the URL.
    //
    // OLD:
    //   const res = yield PROCTORING.post("/auth/platform/handoff");
    //   const code = res?.data?.code;
    //   if (!code) { throw new Error("No handoff code returned."); }
    //   const target = `${generateUrl("incproctor")}/#c=${encodeURIComponent(code)}`;
    //
    // WHY. /auth/platform/handoff WAS NEVER BUILT — verified against origin/dev of
    // proctoring-sdk-backend on 2026-09-07: app/modules/ holds admin, browser, client_ui, clients,
    // credits, docs, health, me and metrics, there is no bridge module, and "handoff" appears
    // nowhere under app/. Every click 404'd. What DOES exist is POST /admin/v1/auth/platform
    // (app/modules/admin/route.py), and it works differently and better: it reads the InCruiter
    // admin token from the Authorization header, asks the InCruiter auth system whether that token
    // belongs to an operator, and on yes mints its OWN staff session, returned as an HttpOnly
    // cookie. Same session /auth/login issues, so every role check and audit line downstream is
    // unchanged.
    //
    // SO THERE IS NOTHING TO PUT IN A URL. The old design carried a live single-use credential in
    // the address bar. This one carries nothing: there is no link that can be pasted, forwarded or
    // read off a screen-share to get in. The only way through is to already hold an InCruiter
    // operator token, and that never leaves the Authorization header.
    //
    // The cookie is HttpOnly, SameSite=Lax and Secure outside dev. Lax is sufficient because both
    // hosts sit under incruiter.com, which makes them SAME-SITE even though they are different
    // origins — SameSite is decided by the registrable domain, not the origin.
    yield PROCTORING.post("/auth/platform");

    // A 2xx means the session cookie is now set for the console's origin. Nothing is read out of
    // the response on purpose: the credential IS the cookie, and depending on a body the browser
    // has already acted on would only invent a second source of truth.
    yield put(proctorHandoffSuccess());

    // [2026-09-07] CHANGED — this is DELIBERATELY a different host from the one the call went to.
    //
    // OLD: // The SAME origin the call above went to — see the invariant note in
    //      // utils/api/proctoring.jsx.
    //
    // That note claimed the two had to share an origin. They do not, and they cannot: the API and
    // the staff console are separate hosts (see the three-host list in utils/api/proctoring.jsx).
    // The POST above goes to the API, which sets the session cookie for ITSELF; the console then
    // presents that cookie on its own calls to the same API. The redirect only has to land on the
    // console.
    const target = generateUrl("incproctor_admin");
    if (typeof payload.onSuccess === "function") {
      yield call(payload.onSuccess, target);
    } else {
      window.location.href = target;
    }
  } catch (e) {
    // Distinguish "the service answered and said no" from "the request never arrived". They need
    // completely different fixes, and one generic message sends people looking in the wrong place —
    // a rejected CORS preflight, for instance, produces no `response` at all and used to surface as
    // a bare "please try again".
    //
    // [2026-09-07] ADDED — 503 and 401 are named separately, because they are the two failures this
    // will actually produce and they are fixed by completely different people. 503 is a deployment
    // where nobody has switched the bridge on; 401 is this operator not being recognised. Telling
    // an operator "you are not authorised" when the truth is "this is not enabled yet" sends them
    // to ask for access they already have.
    const status = e?.response?.status;
    let msg;
    if (status === 503) {
      msg =
        "One-click sign-in is not switched on for IncProctor in this environment yet. You can " +
        "still open the console and sign in with your IncProctor staff password.";
    } else if (status === 401) {
      msg =
        "IncProctor did not recognise your InCruiter account as an operator. If you had access " +
        "before, your staff account there may have been disabled.";
    } else if (e?.response) {
      msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        `IncProctor refused the request (${status}).`;
    } else {
      msg =
        "Could not reach the IncProctor service. It may be offline, or this site may not be an " +
        "allowed origin for it.";
    }
    yield put(proctorHandoffFailure(msg));
    if (typeof payload.onFailure === "function") {
      yield call(payload.onFailure, msg);
    }
  }
}

export function* watchProctoringAPI() {
  yield takeEvery(PROCTOR_HANDOFF, proctorHandoffRequest);
}

export default function* proctoringSaga() {
  yield all([watchProctoringAPI()]);
}
